import React, { FC, ReactElement } from 'react';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import ControlGroupTip from '../ControlGroup/ControlGroupTip';
import { isNullOrUndefined } from '../../../predicates/type';
import { ControlGroupRowProps, GridTemplate } from './types';
import { getGridColumns, getGridColumnsCount } from './utils';

/**
 * Aligns its child components into a row.
 * A row is divided into six columns.
 * Each child component may take space of one or more columns.
 */
const ControlGroupRow: FC<React.PropsWithChildren<ControlGroupRowProps>> = ({
	children,
	className = '',
	grid,
	title,
	titleLight = true,
	titleWhite,
	isRequired,
	tipMessage,
	itemsFlexEnd,
	itemsFlexCenter,
	itemsClassName,
}) => {
	const componentClasses = classNames(classes.controlGroupRow, className, {
		[classes.controlGroupRow_required]: isRequired,
	});

	const titleClasses = classNames(classes.controlGroupRow__title, {
		[classes.controlGroupRow__title_light]: titleLight,
		[classes.controlGroupRow__title_white]: titleWhite,
	});

	const itemsClasses = classNames(classes.controlGroupRow__items, itemsClassName, {
		[classes.controlGroupRow__items_flexEnd]: itemsFlexEnd,
		[classes.controlGroupRow__items_flexCenter]: itemsFlexCenter,
	});

	const templateParts = grid.split('-') as GridTemplate;

	const gridColumns = getGridColumns(templateParts);
	const columnsCount = getGridColumnsCount(templateParts);

	return (
		<div className={componentClasses}>
			{title && (
				<div className={classes.titleSection}>
					<div className={titleClasses}>{title}</div>
					{tipMessage && <ControlGroupTip tipMessage={tipMessage} />}
				</div>
			)}
			<div
				className={itemsClasses}
				style={{ gridTemplateColumns: `repeat(${columnsCount}, 1fr)` }}
			>
				{React.Children.map(children, (child, index) => {
					if (isNullOrUndefined(child)) {
						return null;
					}

					const itemClasses = classNames(
						classes.controlGroupRow__item,
						(child as ReactElement).props?.className,
					);

					return (
						<div
							className={itemClasses}
							style={{ gridColumn: gridColumns[index] }}
						>
							{child}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export { ControlGroupRow };
